/**
 * useSaveTarget
 *
 * Remembers which saved record the user is currently working *from*, so that saving after a load
 * can offer to update it instead of silently creating a duplicate.
 *
 * Without this, the save flow has no memory: load "Mesh Concept", change WR2 to a slant, hit save,
 * and you get a second row also called "Mesh Concept" — the edit is lost as an edit and the list
 * fills with near-identical copies. The only fix available to the user is to delete the old one by
 * hand, and only if they notice.
 *
 * The hook holds three things:
 *
 *  - **which** record is being worked from (`id`, `name`),
 *  - **what it looked like** when it was loaded or last saved (`snapshot`),
 *  - whether the working copy has since diverged (`isDirty`).
 *
 * `isDirty` is what makes the prompt honest. Offering "Update" when nothing has changed is noise,
 * and offering it when the user has already saved is worse — it implies unsaved work that does not
 * exist. Re-snapshotting on every successful save (see `track`) keeps the two in step.
 *
 * Generic over the payload so the offensive play manager and the defensive one share one
 * implementation and one set of tests. Neither knows anything about the other's shape.
 */

import { useCallback, useState } from 'react';

/** The record the working copy came from, plus how it looked at that moment. */
export interface SaveTarget<T> {
  id: string;
  name: string;
  /** The payload as saved — compared against the working copy to detect edits. */
  snapshot: T;
}

export interface SaveTargetHandle<T> {
  /** The record being worked from, or null when this is fresh unsaved work. */
  target: SaveTarget<T> | null;
  /** True when a record is being worked from AND the working copy differs from it. */
  isDirty: boolean;
  /** Start (or restart) tracking a record — on load, and again after every successful save. */
  track: (target: SaveTarget<T>) => void;
  /** Stop tracking entirely; the next save creates a new record. */
  clear: () => void;
  /** Stop tracking only if `id` is the tracked one — for deletions. */
  forget: (id: string) => void;
}

/**
 * Structural equality for the flat, plain-data payloads these managers save (strings nested at
 * most one level deep). Deliberately not a general deep-equal: no dates, no cycles, no class
 * instances, because nothing here has them.
 */
export function payloadsEqual(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (typeof a !== 'object' || typeof b !== 'object' || a === null || b === null) return false;

  const ak = Object.keys(a as object);
  const bk = Object.keys(b as object);
  if (ak.length !== bk.length) return false;

  return ak.every(
    (k) =>
      Object.prototype.hasOwnProperty.call(b, k) &&
      payloadsEqual((a as Record<string, unknown>)[k], (b as Record<string, unknown>)[k])
  );
}

/**
 * @param current the working copy, re-read on every render so `isDirty` stays live as the user
 *                edits. Deriving it rather than storing it means no effect has to keep a
 *                `hasChanges` flag in sync — there is nothing to go stale.
 */
export function useSaveTarget<T>(current: T): SaveTargetHandle<T> {
  const [target, setTarget] = useState<SaveTarget<T> | null>(null);

  const track = useCallback((next: SaveTarget<T>) => setTarget(next), []);
  const clear = useCallback(() => setTarget(null), []);
  const forget = useCallback(
    (id: string) => setTarget((t) => (t && t.id === id ? null : t)),
    []
  );

  return {
    target,
    isDirty: target !== null && !payloadsEqual(target.snapshot, current),
    track,
    clear,
    forget,
  };
}
